import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import Footer from '../Footer';
import './AzureComputeOptimiser.css';
import { apiRequest } from '../../authConfig';

const API_BASE = 'https://azuresizingcalculator-api.coretez.io';

const FILTERS = [
  { key: 'all',           label: 'All' },
  { key: 'Idle',          label: 'Idle (Low Hanging Fruit)' },
  { key: 'Underutilised', label: 'Underutilised' },
  { key: 'Moderate',      label: 'Moderate' },
  { key: 'Healthy',       label: 'Healthy' },
  { key: 'Constrained',   label: 'Constrained' },
];

function badgeClass(status) {
  const map = {
    Idle: 'badge-idle',
    Underutilised: 'badge-underutilised',
    Moderate: 'badge-moderate',
    Healthy: 'badge-healthy',
  };
  return `badge ${map[status] || ''}`;
}

function fmtPct(val) {
  if (val == null) return <span className="no-action">N/A</span>;
  return `${val}%`;
}

export default function AzureComputeOptimiser() {
  const { instance, accounts } = useMsal();
  const fileInputRef = useRef(null);
  const [file, setFile]         = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [results, setResults]   = useState(null);
  const [filter, setFilter]     = useState('all');
  const [mode, setMode]         = useState('safe'); // 'safe' | 'aggressive'
  const [currency, setCurrency] = useState('USD'); // 'USD' | 'GBP'
  const [fxRate, setFxRate]     = useState(null);  // USD → GBP rate

  // ── auth token ─────────────────────────────────────────────────────────
  // Uses MSAL acquireTokenSilent to get an access token scoped to this API.
  // The access token has aud = MSAL_CLIENT_ID, which the backend validates.
  // Falls back to a redirect login if the silent call requires interaction.
  async function getToken() {
    if (!accounts || accounts.length === 0) return null;
    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Session expired — redirect to login
        await instance.acquireTokenRedirect({ ...apiRequest, account: accounts[0] });
      }
      return null;
    }
  }

  // ── file selection ──────────────────────────────────────────────────────
  const pickFile = (f) => {
    if (f && f.name.endsWith('.csv')) {
      setFile(f);
      setError(null);
      setResults(null);
    } else {
      setError('Please select a valid .csv file');
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    pickFile(f);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── analyse ─────────────────────────────────────────────────────────────
  async function analyse() {
    if (!file) { setError('Please select a CSV file first'); return; }
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const token = await getToken();
      if (!token) {
        setError('No auth token found — please sign in and try again.');
        return;
      }

      const form = new FormData();
      form.append('csv', file);

      let res;
      try {
        res = await fetch(`${API_BASE}/api/compute-optimiser/analyse`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
      } catch (networkErr) {
        // Network-level failure: CORS block, DNS failure, API unreachable
        // Try a health check to give a more specific message
        let healthOk = false;
        try {
          const hc = await fetch(`${API_BASE}/health`, { signal: AbortSignal.timeout(5000) });
          healthOk = hc.ok;
        } catch { /* health check also failed */ }

        const isOffline = !navigator.onLine;
        if (isOffline) {
          setError('You appear to be offline. Please check your connection.');
        } else if (healthOk) {
          setError(`The API is reachable but the request was blocked — this may be a CORS or auth issue. (${networkErr.message})`);
        } else {
          setError(`Cannot reach the API at ${API_BASE}. The service may be starting up (Container Apps scale from zero) — please wait 30 seconds and try again. (${networkErr.message})`);
        }
        return;
      }

      // Try to parse JSON; fall back to raw text for non-JSON error responses
      let json;
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        json = await res.json();
      } else {
        const text = await res.text();
        throw new Error(`Unexpected response (HTTP ${res.status}): ${text.slice(0, 200)}`);
      }

      if (res.status === 401) {
        throw new Error('Authentication failed — your session may have expired. Please sign out and sign back in.');
      }
      if (res.status === 413) {
        throw new Error('CSV file is too large (10 MB limit). Please reduce the number of rows and try again.');
      }
      if (!res.ok) {
        throw new Error(json.error || `API error (HTTP ${res.status})`);
      }

      setResults(json.results);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── derived data ─────────────────────────────────────────────────────────
  // Fetch live USD→GBP rate when results arrive (free, no auth required)
  useEffect(() => {
    if (!results) return;
    fetch('https://api.frankfurter.app/latest?from=USD&to=GBP')
      .then(r => r.json())
      .then(d => { if (d?.rates?.GBP) setFxRate(d.rates.GBP); })
      .catch(() => { setFxRate(0.79); }); // fall back to approximate rate
  }, [results]);

  // Format a USD amount into the selected currency
  function fmtMoney(usdAmount) {
    if (usdAmount == null) return null;
    if (currency === 'GBP' && fxRate) {
      return `£${(usdAmount * fxRate).toFixed(2)}`;
    }
    return `$${usdAmount.toFixed(2)}`;
  }
  const filtered = results
    ? filter === 'all'         ? results
    : filter === 'Constrained' ? results.filter(r => r.isConstrained)
    : results.filter(r => r.status === filter)
    : [];

  const totalSaving = results
    ? results.reduce((sum, r) => {
        const s = mode === 'safe'
          ? r.estimatedMonthlySaving
          : r.aggressiveEstimatedMonthlySaving ?? r.estimatedMonthlySaving;
        return sum + (s || 0);
      }, 0)
    : 0;

  const idleCount = results ? results.filter(r => r.status === 'Idle').length : 0;
  const actionableCount = results
    ? results.filter(r => r.status !== 'Healthy').length
    : 0;

  // ── CSV download ──────────────────────────────────────────────────────────
  function downloadCSV() {
    if (!filtered.length) return;
    const header = [
      'Resource Name', 'Resource Group', 'VM Size', 'vCPUs', 'RAM GB', 'SKU Max IOPS', 'Region',
      'CPU Max %', 'CPU Avg %', 'Total RAM GB', 'Mem Peak Used %', 'Mem Avg %', 'IOPS Util %',
      'Status', 'Memory Status', 'IOPS Status', 'Constrained',
      'Recommendation', 'Current $/mo', 'Safe Target', 'Safe $/mo',
      'Aggressive Target', 'Aggressive $/mo', 'Est. Monthly Saving ($)',
    ].join(',');
    const rec = r => mode === 'safe' ? r.safeRecommendation : (r.aggressiveRecommendation ?? r.safeRecommendation);
    const rows = filtered.map(r => [
      r.name, r.rg, r.vmSize,
      r.vmCpuCores ?? '', r.vmMemGB != null ? `${r.vmMemGB} GB` : '', r.skuMaxIops ?? '',
      r.region,
      r.cpuMax, r.cpuAvg,
      r.totalRamGB ?? '',
      r.memUsedPctMax ?? '',
      r.memUsedPctAvg ?? '',
      r.iopsUtilPct ?? '',
      r.status, r.memoryStatus ?? '', r.iopsStatus ?? '',
      r.isConstrained ? 'Yes' : 'No',
      `"${rec(r)}"`,
      r.currentMonthlyCost ?? '',
      r.safeTarget ?? '', r.safeMonthlyCost ?? '',
      r.aggressiveTarget ?? '', r.aggressiveMonthlyCost ?? '',
      (mode === 'safe' ? r.estimatedMonthlySaving : r.aggressiveEstimatedMonthlySaving) ?? '',
    ].join(','));
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'compute-optimiser-results.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="compute-optimiser">
        <h1>Azure Compute Optimiser</h1>
        <p className="subtitle">
          Upload a VM utilisation export to identify right-sizing opportunities and estimate potential savings.
        </p>

        <div className="privacy-banner">
          <i className="fas fa-lock"></i>
          <span>Your data is processed entirely in memory and is <strong>never stored</strong> on our servers.</span>
        </div>

        {/* Upload zone */}
        <div
          className={`upload-zone${dragOver ? ' drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
          aria-label="Upload CSV file"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
          <i className="fas fa-file-csv"></i>
          <p><strong>Click to upload</strong> or drag and drop a CSV file</p>
          {file && <p className="file-chosen"><i className="fas fa-check-circle"></i> {file.name}</p>}
        </div>

        <div className="csv-columns-info">
          <strong>Expected columns:</strong>{' '}
          <code>ResourceName</code>, <code>VMSize</code>, <code>CPU_Max</code>, <code>CPU_Avg</code>{' '}
          (required) — plus optional <code>ResourceGroup</code>, <code>Location</code>,{' '}
          <code>VCPUs</code>, <code>TotalRAM_GB</code>, <code>MemUsed_Pct_Max</code>, <code>MemUsed_Pct_Avg</code>, <code>DiskIOPS_Max</code>, <code>DiskIOPS_Avg</code>.
          Column names are matched flexibly.
        </div>

        <button className="btn-analyse" onClick={analyse} disabled={loading || !file}>
          {loading
            ? <><span className="spinner" style={{width:16,height:16,borderWidth:2}}/> Analysing…</>
            : <><i className="fas fa-bolt"></i> Run Analysis</>}
        </button>

        {error && <div className="analyse-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

        {/* Results */}
        {results && (
          <div className="results-section">
            <div className="results-summary">
              <div className="summary-card">
                <div className="sc-value">{results.length}</div>
                <div className="sc-label">VMs Analysed</div>
              </div>
              <div className="summary-card">
                <div className="sc-value">{idleCount}</div>
                <div className="sc-label">Idle (act now)</div>
              </div>
              <div className="summary-card">
                <div className="sc-value">{actionableCount}</div>
                <div className="sc-label">Actionable</div>
              </div>
              {totalSaving > 0 && (
                <div className="summary-card">
                  <div className="sc-value">
                    {currency === 'GBP' && fxRate
                      ? `£${(totalSaving * fxRate).toFixed(0)}`
                      : `$${totalSaving.toFixed(0)}`}
                  </div>
                  <div className="sc-label">Est. Monthly Saving</div>
                </div>
              )}
            </div>

            <div className="results-controls">
              <div className="filter-group">
                {FILTERS.map(f => (
                  <button
                    key={f.key}
                    className={`btn-filter${filter === f.key ? ' active' : ''}`}
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="mode-group">
                <button
                  className={`btn-mode${mode === 'safe' ? ' active' : ''}`}
                  onClick={() => setMode('safe')}
                >
                  Safe
                </button>
                <button
                  className={`btn-mode${mode === 'aggressive' ? ' active' : ''}`}
                  onClick={() => setMode('aggressive')}
                >
                  Aggressive
                </button>
              </div>
              <div className="mode-group">
                <button
                  className={`btn-mode${currency === 'USD' ? ' active' : ''}`}
                  onClick={() => setCurrency('USD')}
                  title="Show prices in US Dollars"
                >
                  $ USD
                </button>
                <button
                  className={`btn-mode${currency === 'GBP' ? ' active' : ''}${!fxRate ? ' disabled' : ''}`}
                  onClick={() => fxRate && setCurrency('GBP')}
                  title={fxRate ? `Show prices in British Pounds (rate: ${fxRate})` : 'Loading exchange rate…'}
                  disabled={!fxRate}
                >
                  £ GBP
                </button>
              </div>
              <button className="btn-csv-download" onClick={downloadCSV}>
                <i className="fas fa-download"></i> Export CSV
              </button>
            </div>

            <div className="results-table-wrap">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Resource Name</th>
                    <th>Resource Group</th>
                    <th>VM Size</th>
                    <th>vCPUs</th>
                    <th>RAM GB</th>
                    <th>SKU IOPS</th>
                    <th>Region</th>
                    <th>CPU Max</th>
                    <th>CPU Avg</th>
                    <th>Mem Peak %</th>
                    <th>Mem Avg %</th>
                    <th>IOPS Util %</th>
                    <th>Status</th>
                    <th>{mode === 'safe' ? 'Safe Recommendation' : 'Aggressive Recommendation'}</th>
                    <th>Current {currency === 'GBP' ? '£' : '$'}/mo</th>
                    <th>Est. Saving/mo</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={16} style={{ textAlign: 'center', color: '#445566', padding: '24px' }}>
                        No results match this filter.
                      </td>
                    </tr>
                  ) : filtered.map((r, i) => (
                    <tr key={i}>
                      <td>{r.name}</td>
                      <td>{r.rg || <span className="no-action">—</span>}</td>
                      <td><code style={{ fontSize: '0.8em' }}>{r.vmSize || '—'}</code></td>
                      <td>{r.vmCpuCores ?? <span className="no-action">—</span>}</td>
                      <td>{r.vmMemGB != null ? `${r.vmMemGB} GB` : <span className="no-action">—</span>}</td>
                      <td>{r.skuMaxIops != null ? r.skuMaxIops.toLocaleString() : <span className="no-action">—</span>}</td>
                      <td>{r.region}</td>
                      <td>{r.cpuMax.toFixed(1)}%</td>
                      <td>{r.cpuAvg.toFixed(1)}%</td>
                      <td>{fmtPct(r.memUsedPctMax)}</td>
                      <td>{fmtPct(r.memUsedPctAvg)}</td>
                      <td>{r.iopsUtilPct != null ? `${r.iopsUtilPct}%` : <span className="no-action">N/A</span>}</td>
                      <td>
                        <span className={badgeClass(r.status)}>{r.status}</span>
                        {r.isConstrained && <span className="badge badge-constrained" style={{ marginLeft: 4 }}>Constrained</span>}
                      </td>
                      <td style={{ maxWidth: 240 }}>
                        {mode === 'safe' ? r.safeRecommendation : r.aggressiveRecommendation}
                      </td>
                      <td>
                        {r.currentPricePerHour != null
                          ? fmtMoney(r.currentPricePerHour * 24 * 30)
                          : <span className="no-action">—</span>}
                      </td>
                      <td>
                        {r.estimatedMonthlySaving != null
                          ? <span className="saving-value">
                              {fmtMoney(mode === 'safe'
                                ? r.estimatedMonthlySaving
                                : r.aggressiveEstimatedMonthlySaving ?? r.estimatedMonthlySaving
                              )}
                            </span>
                          : <span className="no-action">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
